/**
 * Tenant attendance bands for the dashboard Check-ins chart (WSRADM-0039).
 *
 * `GET`/`PUT /admin/reports/attendance-bands` via `apiBase()`. Tenant is the JWT —
 * never a slug on the path, query, or header. Bearer only; no cookies, no CSRF.
 * Never `/admin/platform/**`. `default` is a server flag — render it, never infer
 * it from the list contents. PUT body is the full ordered `{ bands }` list;
 * `[]` resets to the platform default. Response shape matches GET.
 */
import { request } from '@/api/client';
import { ApiError } from '@/api/errors';
import { asBoolean, asNumber, asText, isObject } from '@/api/parse';

export interface AttendanceBandDto {
  label: string;
  fromHour: number;
  toHour: number;
}

export interface AttendanceBandsDto {
  bands: AttendanceBandDto[];
  default: boolean;
}

function asHour(value: unknown): number | null {
  const n = asNumber(value);
  if (n === null || !Number.isInteger(n)) return null;
  return n;
}

function parseBand(raw: unknown): AttendanceBandDto | null {
  if (!isObject(raw)) return null;
  const label = asText(raw['label']);
  const fromHour = asHour(raw['fromHour']);
  const toHour = asHour(raw['toHour']);
  if (label === null || fromHour === null || toHour === null) return null;
  return { label, fromHour, toHour };
}

export function parseAttendanceBands(body: unknown): AttendanceBandsDto {
  if (!isObject(body)) {
    throw new ApiError('UNKNOWN', 0, {});
  }
  const bands: AttendanceBandDto[] = [];
  if (Array.isArray(body['bands'])) {
    for (const entry of body['bands']) {
      const band = parseBand(entry);
      if (band !== null) bands.push(band);
    }
  }
  return {
    bands,
    default: asBoolean(body['default']) === true,
  };
}

/** GET /admin/reports/attendance-bands — never 404; always `{ bands, default }`. */
export async function getAttendanceBands(): Promise<AttendanceBandsDto> {
  const body = await request<unknown>('GET', '/admin/reports/attendance-bands');
  return parseAttendanceBands(body);
}

/** PUT /admin/reports/attendance-bands — body `{ bands }` full list; `[]` resets. */
export async function putAttendanceBands(
  bands: AttendanceBandDto[],
): Promise<AttendanceBandsDto> {
  const body = await request<unknown>('PUT', '/admin/reports/attendance-bands', {
    json: { bands },
  });
  return parseAttendanceBands(body);
}

/** Backend envelope code `INVALID_ATTENDANCE_BANDS` (details['code']). */
export function isInvalidAttendanceBands(err: unknown): boolean {
  return err instanceof ApiError && err.details?.['code'] === 'INVALID_ATTENDANCE_BANDS';
}
